import type { StepSchema } from '../types';
import { useFormContext } from '../context/FormContext';
import { SectionView } from './SectionView';

export function StepView({ stepSchema }: { stepSchema: StepSchema }) {
  const {
    formService,
    displayService,
    validationService,
    stateService,
    scrollService,
    settings,
    forceUpdate,
  } = useFormContext();

  const step = formService.getFormData().steps.find((s) => s.id === stepSchema.id);

  if (!step || !formService.isActiveStep(step)) {
    return null;
  }

  const continueAlwaysEnabled = settings.continueAlwaysEnabled ?? false;
  const hideStepTitlesWithQuestions = settings.hideStepTitlesWithQuestions ?? false;

  const hasViewableQuestions = step.hasViewableQuestions(displayService);
  const isComplete = step.isComplete(displayService);
  const isFinalStep = formService.isFinalStep(step);
  const isFirstStep = formService.isFirstStep(step);

  const showContinueButton =
    !isFinalStep &&
    (continueAlwaysEnabled || isComplete) &&
    stateService.canProceedToNextStep(formService);

  const enableContinueButton = continueAlwaysEnabled || isComplete;

  const showTitle = hideStepTitlesWithQuestions ? !hasViewableQuestions : true;

  const continueStep = () => {
    stateService.showErrors = !step.isValid(displayService, validationService);

    if (stateService.showErrors) {
      // Re-render so the validation messages (which depend on
      // stateService.showErrors) actually appear before we scroll to them.
      forceUpdate();

      setTimeout(() => {
        const msgElement = document.querySelector(
          `#${step.id} .validation-msg.active`
        )?.previousElementSibling;

        if (msgElement) {
          scrollService.scrollElementTo(msgElement);
        }
      }, 300);
      return;
    }

    formService.setNextActiveStep();
    forceUpdate();
    scrollService.stepScrollToTop();
  };

  const previousStep = () => {
    formService.setPreviousActiveStep();
    forceUpdate();
    scrollService.stepScrollToTop();
  };

  return (
    <div className="step" id={step.id}>
      {showTitle && <h3>{step.title}</h3>}

      {stepSchema.sections.map((sectionSchema) => (
        <SectionView key={sectionSchema.id} sectionSchema={sectionSchema} step={step} />
      ))}

      {!hasViewableQuestions && !isFinalStep && (
        <p>No questions required for this step. Please continue.</p>
      )}

      <hr className="pull-right" />
      <div className="buttons cf">
        <div>
          {!isFirstStep && !isFinalStep && (
            <button type="button" onClick={previousStep} className="button bordered">
              Back
            </button>
          )}
        </div>
        <div>
          {showContinueButton && (
            <button
              type="button"
              onClick={continueStep}
              disabled={!enableContinueButton}
              className="button button--primary"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
